export type MovementId = 'squat' | 'pushup' | 'bridge' | 'plank' | 'lunge' | 'march';
export type StepKind = 'welcome' | 'prepare' | 'reps' | 'timer' | 'rest' | 'complete';

export interface Level {
  id: number;
  name: string;
  rounds: 2 | 3;
  squats: number;
  pushups: number;
  bridges: number;
  plank: number;
  lunges: number;
  march: number;
  rest: number;
  prepare: number;
}

export interface Step {
  id: string;
  kind: StepKind;
  movement?: MovementId;
  title: string;
  detail: string;
  modification?: string;
  round: number;
  rounds: number;
  durationMs: number;
  reps?: number;
  cadenceMs?: number;
  eachSide?: boolean;
  nextTitle?: string;
}

export const LEVELS: Level[] = [
  { id: 0, name: 'Easier', rounds: 2, squats: 8, pushups: 4, bridges: 10, plank: 20, lunges: 4, march: 45, rest: 90, prepare: 20 },
  { id: 1, name: 'Foundation', rounds: 2, squats: 10, pushups: 6, bridges: 12, plank: 25, lunges: 6, march: 60, rest: 90, prepare: 24 },
  { id: 2, name: 'Build', rounds: 2, squats: 12, pushups: 8, bridges: 14, plank: 30, lunges: 8, march: 60, rest: 75, prepare: 18 },
  { id: 3, name: 'Three rounds', rounds: 3, squats: 10, pushups: 6, bridges: 12, plank: 25, lunges: 6, march: 60, rest: 60, prepare: 10 },
  { id: 4, name: 'Steady', rounds: 3, squats: 15, pushups: 10, bridges: 15, plank: 35, lunges: 8, march: 60, rest: 60, prepare: 8 },
];

export const MOVEMENT_INFO: Record<MovementId, { title: string; detail: string; modification: string; cadence: number }> = {
  squat: {
    title: 'Squats',
    detail: 'Feet comfortable, hips back, knees track with toes. Stand tall without rushing.',
    modification: 'Use a chair target or reduce depth.',
    cadence: 6700,
  },
  pushup: {
    title: 'Regular push-ups',
    detail: 'Hands just wider than shoulders. Keep a long line and lower with control.',
    modification: 'Use a counter or knees while keeping your trunk steady.',
    cadence: 7500,
  },
  bridge: {
    title: 'Glute bridges',
    detail: 'Feet planted, ribs quiet. Squeeze glutes to lift, then lower slowly.',
    modification: 'Use a smaller, comfortable lift.',
    cadence: 6000,
  },
  plank: {
    title: 'Plank',
    detail: 'Elbows under shoulders. Make one long line and breathe steadily.',
    modification: 'Place knees down or use a counter.',
    cadence: 1000,
  },
  lunge: {
    title: 'Reverse lunges',
    detail: 'Step back softly, lower in a stable range, then drive through the front foot.',
    modification: 'Hold a chair, shorten the step, or reduce the range.',
    cadence: 7500,
  },
  march: {
    title: 'Fast march / jog in place',
    detail: 'Stay tall. Choose a brisk pace that raises breathing without going all-out.',
    modification: 'March without impact and slow down as needed.',
    cadence: 1000,
  },
};

const order: MovementId[] = ['squat', 'pushup', 'bridge', 'plank', 'lunge', 'march'];

function activeStep(level: Level, movement: MovementId, round: number, testMode: boolean): Step {
  const info = MOVEMENT_INFO[movement];
  const values = {
    squat: level.squats,
    pushup: level.pushups,
    bridge: level.bridges,
    plank: level.plank,
    lunge: level.lunges,
    march: level.march,
  };
  const isReps = movement !== 'plank' && movement !== 'march';
  const eachSide = movement === 'lunge';
  const reps = isReps ? values[movement] * (eachSide ? 2 : 1) : undefined;
  // The mature level uses a still-controlled, ordinary cadence to keep three rounds near 25 minutes.
  const cadence = Math.round(info.cadence * (level.id >= 4 ? 0.7 : 1));
  const durationMs = testMode
    ? 700
    : isReps
      ? (reps ?? 0) * cadence
      : values[movement] * 1000;
  return {
    id: `r${round}-${movement}`,
    kind: isReps ? 'reps' : 'timer',
    movement,
    title: info.title,
    detail: info.detail,
    modification: info.modification,
    round,
    rounds: level.rounds,
    durationMs,
    reps,
    cadenceMs: isReps ? (testMode ? Math.max(100, durationMs / (reps ?? 1)) : cadence) : undefined,
    eachSide,
  };
}

export function generateRoutine(level: Level, testMode = false): Step[] {
  const steps: Step[] = [{
    id: 'welcome', kind: 'welcome', title: 'Get ready',
    detail: 'Clear some space, breathe normally, and choose easier options whenever useful.',
    round: 1, rounds: level.rounds, durationMs: testMode ? 300 : 45_000,
  }];
  for (let round = 1; round <= level.rounds; round += 1) {
    order.forEach((movement, index) => {
      const active = activeStep(level, movement, round, testMode);
      const info = MOVEMENT_INFO[movement];
      steps.push({
        id: `r${round}-prepare-${movement}`, kind: 'prepare', movement,
        title: `Next: ${info.title}`, detail: info.detail, modification: info.modification,
        round, rounds: level.rounds, durationMs: testMode ? 300 : level.prepare * 1000,
        nextTitle: info.title,
      });
      steps.push(active);
      const next = order[index + 1];
      if (next) active.nextTitle = MOVEMENT_INFO[next].title;
    });
    if (round < level.rounds) {
      steps.push({
        id: `r${round}-rest`, kind: 'rest', title: 'Rest',
        detail: 'Walk around, shake out tension, and let your breathing settle.',
        round, rounds: level.rounds, durationMs: testMode ? 500 : level.rest * 1000,
        nextTitle: `Round ${round + 1}`,
      });
    }
  }
  steps.push({
    id: 'complete', kind: 'complete', title: 'Workout complete',
    detail: 'Nice work. Let your breathing settle and notice how the effort felt.',
    round: level.rounds, rounds: level.rounds, durationMs: 0,
  });
  return steps;
}

export function routineDuration(steps: Step[]): number {
  return steps.reduce((sum, step) => sum + step.durationMs, 0);
}

export function formatTime(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
